import { Trans } from "@lingui/macro";
import { useMemo } from "react";
import { ethers } from "ethers";

import { ARBITRUM, BSC } from "config/chains";
import { USD_DECIMALS } from "config/factors";
import { getTokenBySymbol } from "config/tokens";
import { useV1FeesInfo, useTotalVolume } from "domain/stats";
import { useVolumeData } from "domain/legacy";
import useUniqueUsers from "domain/stats/useUniqueUsers";
import useV2Stats from "domain/synthetics/stats/useV2Stats";
import { useInfoTokens } from "domain/tokens";
import { bigMath } from "lib/bigmath";
import { useChainId } from "lib/chains";
import { MLP_DECIMALS } from "lib/legacy";
import { expandDecimals, formatAmount } from "lib/numbers";
import { sumBigInts } from "lib/sumBigInts";
import useWallet from "lib/wallets/useWallet";
import type { ChainStats } from "./useDashboardChainStatsMulticall";

import ChainsStatsTooltipRow from "components/StatsTooltip/ChainsStatsTooltipRow";
import TooltipComponent from "components/Tooltip/Tooltip";

const ethTreasuryFund = expandDecimals(350 + 148 + 384, 18);
const mlpTreasuryFund = expandDecimals(660001, 18);
const usdcTreasuryFund = expandDecimals(784598 + 200000, 30);

export function StatsCard({
  statsArbitrum,
  statsBsc,
}: {
  statsArbitrum?: ChainStats;
  statsBsc?: ChainStats;
}) {
  const { active } = useWallet();
  const { chainId } = useChainId();
  // const v1TotalVolume = useTotalVolume();
  const [totalVolume, totalVolumeDelta] = useVolumeData();

  const v2ArbitrumOverview = useV2Stats(ARBITRUM);
  const v2BscOverview = useV2Stats(BSC);

  const uniqueUsers = useUniqueUsers();

  const suppliesArbitrum = statsArbitrum?.reader.tokenBalancesWithSupplies;
  const suppliesBsc = statsBsc?.reader.tokenBalancesWithSupplies;

  const { infoTokens: infoTokensArbitrum } = useInfoTokens(undefined, ARBITRUM, active, undefined, undefined);
  const { infoTokens: infoTokensRon } = useInfoTokens(undefined, BSC, active, undefined, undefined);
  const infoTokens = chainId === ARBITRUM ? infoTokensArbitrum : infoTokensRon;
  const eth = infoTokens[getTokenBySymbol(chainId === ARBITRUM ? ARBITRUM : BSC, "ETH").address];

  const v1ArbitrumTotalFees = useV1FeesInfo(ARBITRUM);
  const v1BscTotalFees = useV1FeesInfo(BSC);
  const totalFeesUsd = sumBigInts(
    // v1ArbitrumTotalFees?.totalFees,
    v1BscTotalFees?.totalFees,
    // v2ArbitrumOverview.totalFees,
    // v2BscOverview.totalFees
  );

  // #endregion Fees

  // #region Treasury
  const mlpTvlArbitrum = statsArbitrum?.mlp.aum;
  const mlpTvlBsc = statsBsc?.mlp.aum;

  const mlpTvl = chainId === ARBITRUM ? mlpTvlArbitrum : mlpTvlBsc;

  const mlpSupplyArbitrum = suppliesArbitrum?.mlpSupply;
  const mlpSupplyBsc = suppliesBsc?.mlpSupply;

  const mlpSupply = chainId === ARBITRUM ? mlpSupplyArbitrum : mlpSupplyBsc;

  const mlpPrice =
    mlpTvl !== undefined && mlpTvl > 0n && mlpSupply !== undefined
      ? bigMath.mulDiv(mlpTvl, expandDecimals(1, MLP_DECIMALS), mlpSupply)
      : expandDecimals(1, USD_DECIMALS);

  let totalTreasuryFundUsd: bigint | undefined = undefined;

  if (eth && eth.contractMinPrice !== undefined && mlpPrice !== undefined) {
    const ethTreasuryFundUsd = bigMath.mulDiv(ethTreasuryFund, eth.contractMinPrice, expandDecimals(1, eth.decimals));

    const mlpTreasuryFundUsd = bigMath.mulDiv(mlpTreasuryFund, mlpPrice, expandDecimals(1, 18));

    totalTreasuryFundUsd = ethTreasuryFundUsd + mlpTreasuryFundUsd + usdcTreasuryFund;
  }

  // #endregion Treasury

  const totalFeesEntries = useMemo(
    () => ({
      // "Ronin": v1ArbitrumTotalFees?.totalFees,
      // "V2 Ronin": v2ArbitrumOverview?.totalFees,
      "Bsc": v1BscTotalFees?.totalFees,
      // "V2 Bsc": v2BscOverview?.totalFees,
    }),
    [
      v1BscTotalFees?.totalFees,
      // v1ArbitrumTotalFees?.totalFees,
      // v2ArbitrumOverview?.totalFees,
      // v2BscOverview?.totalFees,
    ]
  );

  const totalVolumeEntries = useMemo(
    () => ({
      // "Ronin": v1TotalVolume?.[ARBITRUM],
      // "V2 Ronin": v2ArbitrumOverview?.totalVolume,
      "Bsc": ethers.parseUnits((totalVolume ? totalVolume : 0).toString(), USD_DECIMALS),
      // "V2 Bsc": v2BscOverview?.totalVolume,
    }),
    [
      totalVolume, 
      // v2ArbitrumOverview?.totalVolume, 
      // v2BscOverview?.totalVolume
    ]
  );

  const uniqueUsersEnttries = useMemo(
    () => ({
      "Ronin": uniqueUsers?.[ARBITRUM],
      "V2 Ronin": v2ArbitrumOverview?.totalUsers,
      "Bsc": uniqueUsers?.[BSC],
      "V2 Bsc": v2BscOverview?.totalUsers,
    }),
    [uniqueUsers, v2ArbitrumOverview?.totalUsers, v2BscOverview?.totalUsers]
  );

  return (
    <div className="App-card">
      <div className="App-card-title">
        <Trans>Stats</Trans>
      </div>
      <div className="App-card-divider"></div>
      <div className="App-card-content">
        <div className="App-card-row">
          <div className="label">
            <Trans>Fees</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${formatAmount(totalFeesUsd, USD_DECIMALS, 0, true)}`}
              content={<ChainsStatsTooltipRow entries={totalFeesEntries} />}
            />
          </div>
        </div>
        <div className="App-card-row">
          <div className="label">
            <Trans>Volume</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={`$${totalVolume ? totalVolume?.toFixed(2) : 0}`}
              content={<ChainsStatsTooltipRow entries={totalVolumeEntries} />}
            />
          </div>
        </div>
        {/* <div className="App-card-row">
          <div className="label">
            <Trans>Users</Trans>
          </div>
          <div>
            <TooltipComponent
              position="bottom-end"
              className="whitespace-nowrap"
              handle={formatAmount(
                sumBigInts(
                  uniqueUsers?.[ARBITRUM],
                  uniqueUsers?.[BSC],
                  v2ArbitrumOverview?.totalUsers,
                  v2BscOverview?.totalUsers
                ),
                0,
                0,
                true
              )}
              content={<ChainsStatsTooltipRow showDollar={false} shouldFormat={false} entries={uniqueUsersEnttries} />}
            />
          </div>
        </div> */}
        {/* <div className="App-card-row">
          <div className="label">
            <Trans>Treasury</Trans>
          </div>
          <div>${formatAmount(totalTreasuryFundUsd, USD_DECIMALS, 0, true)}</div>
        </div> */}
      </div>
    </div>
  );
}
